import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck, MapPin, Layers } from "lucide-react";
import { Button } from "./ui/button";
import { Link } from "react-router-dom";
const heroBg = "/images/hero-bg-optimized.webp";
import { useRef } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import SplitChars from "./hero/SplitChars";
import TiltGlassCard from "./hero/TiltGlassCard";
import HeroGridLines from "./hero/HeroGridLines";

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();

  const { scrollYProgress } = useScroll(
    isMobile ? undefined : {
      target: sectionRef,
      offset: ["start start", "end start"],
    }
  );

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);

  const eyebrowItems = [
    { icon: Layers, label: "Community Infrastructure Co." },
    { icon: ShieldCheck, label: "MSME Registered · India" },
    { icon: MapPin, label: "Nashik · India + Global" },
  ];

  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 noise-overlay"
    >
      {/* Background Video */}
      <div className="absolute inset-0 z-0">
        {isMobile ? (
          <img
            src={heroBg}
            alt=""
            fetchPriority="high"
            decoding="async"
            width={1024}
            height={576}
            sizes="100vw"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <motion.div style={{ y }} className="absolute inset-0">
            <video
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
              poster={heroBg}
            >
              <source src="/videos/hero-bg.mp4" type="video/mp4" />
            </video>
          </motion.div>
        )}

        {/* Cinematic gradient overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(160deg, 
              hsl(var(--background) / 0.95) 0%, 
              hsl(var(--background) / 0.85) 30%,
              hsl(var(--primary) / 0.07) 55%,
              hsl(var(--background) / 0.92) 80%,
              hsl(var(--background)) 100%
            )`,
          }}
        />

        {/* Morphing gradient blob */}
        {!isMobile && (
          <div
            className="absolute w-[600px] h-[600px] opacity-20 animate-morph-blob"
            style={{
              top: "10%",
              left: "50%",
              transform: "translateX(-50%)",
              background: `radial-gradient(circle, hsl(var(--primary) / 0.5) 0%, hsl(var(--accent) / 0.3) 40%, transparent 70%)`,
              filter: "blur(80px)",
            }}
          />
        )}

        {/* Cinematic light beams — desktop only */}
        {!isMobile && (
          <>
            <motion.div
              animate={{ x: [0, 80, 0], y: [0, -40, 0], scale: [1, 1.2, 1] }}
              transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-1/3 -left-1/4 w-[50%] h-[50%] rounded-full opacity-25"
              style={{
                background: "radial-gradient(circle, hsl(var(--primary) / 0.4) 0%, transparent 65%)",
                filter: "blur(120px)",
              }}
            />
            <motion.div
              animate={{ x: [0, -60, 0], y: [0, 60, 0], scale: [1, 0.85, 1] }}
              transition={{ duration: 30, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-1/4 -right-1/4 w-[55%] h-[55%] rounded-full opacity-20"
              style={{
                background: "radial-gradient(circle, hsl(var(--accent) / 0.5) 0%, transparent 65%)",
                filter: "blur(140px)",
              }}
            />
            {/* Top spotlight */}
            <div 
              className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[40%] opacity-[0.07]"
              style={{
                background: "radial-gradient(ellipse 60% 40% at 50% 0%, hsl(var(--primary)), transparent)",
              }}
            />
          </>
        )}

        {/* Floating particles — enhanced */}
        {!isMobile && [0, 1, 2, 3, 4, 5].map((i) => (
          <motion.div
            key={i}
            animate={{
              opacity: [0, 0.8, 0],
              y: [0, -150 - i * 30, -300],
              x: [0, (i % 2 === 0 ? 20 : -20), 0],
            }}
            transition={{
              duration: 5 + i * 0.8,
              repeat: Infinity,
              delay: i * 0.9,
              ease: "easeInOut",
            }}
            className="absolute rounded-full"
            style={{
              left: `${10 + i * 15}%`,
              top: `${55 + (i % 3) * 12}%`,
              width: `${2 + (i % 3)}px`,
              height: `${2 + (i % 3)}px`,
              background: i % 2 === 0
                ? "hsl(var(--primary))"
                : "hsl(var(--accent))",
              boxShadow: i % 2 === 0
                ? "0 0 12px hsl(var(--primary) / 0.6)"
                : "0 0 12px hsl(var(--accent) / 0.6)",
            }}
          />
        ))}

        {/* Orbiting ring — desktop only */}
        {!isMobile && (
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] opacity-[0.06]"
            style={{
              border: "1px solid hsl(var(--primary) / 0.3)",
              borderRadius: "50%",
            }}
          >
            <div
              className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-primary shadow-lg shadow-primary/50"
            />
          </motion.div>
        )}

        {/* Final vignette */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-transparent to-background" />
      </div>

      {/* Premium smooth grid lines */}
      <HeroGridLines />

      {/* Animated Grid (existing soft pulse layer) */}
      {!isMobile && (
        <motion.div
          animate={{ opacity: [0.08, 0.18, 0.08] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-0 grid-pattern"
        />
      )}

      {/* Scan line */}
      {!isMobile && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: "200%" }}
            transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
            className="absolute top-1/3 h-[1px] w-1/3 bg-gradient-to-r from-transparent via-primary/40 to-transparent"
          />
          {/* Second vertical scan */}
          <motion.div
            initial={{ y: "-100%" }}
            animate={{ y: "200%" }}
            transition={{ duration: 14, repeat: Infinity, ease: "linear", delay: 3 }}
            className="absolute left-2/3 w-[1px] h-1/4 bg-gradient-to-b from-transparent via-accent/20 to-transparent"
          />
        </div>
      )}

      {/* Content */}
      <motion.div 
        style={isMobile ? undefined : { opacity, scale }}
        className="relative z-10 w-full flex items-center justify-center px-4 sm:px-6 lg:px-8"
      >
        <div className="w-full flex items-center justify-center">
          {/* Main Content - Premium Glassmorphism panel with tilt */}
          <TiltGlassCard
            max={isMobile ? 0 : 6}
            glare={isMobile ? 0 : 0.3}
            className="liquid-glass liquid-glass-strong relative w-full max-w-4xl mx-auto text-center rounded-[32px] px-5 sm:px-10 md:px-14 py-10 sm:py-12 md:py-16"
          >
            <div className="liquid-glass-sheen" />
            {/* Liquid glass — animated refraction blob 1 */}
            <motion.div
              aria-hidden
              animate={{ x: [0, 40, -20, 0], y: [0, -30, 20, 0], scale: [1, 1.15, 0.95, 1] }}
              transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
              className="pointer-events-none absolute -top-1/3 -left-1/4 w-[70%] h-[70%] rounded-full opacity-50"
              style={{
                background: "radial-gradient(circle, hsl(var(--primary) / 0.35) 0%, transparent 65%)",
                filter: "blur(60px)",
              }}
            />
            {/* Liquid glass — animated refraction blob 2 */}
            <motion.div
              aria-hidden
              animate={{ x: [0, -30, 25, 0], y: [0, 25, -15, 0], scale: [1, 0.9, 1.1, 1] }}
              transition={{ duration: 22, repeat: Infinity, ease: "easeInOut", delay: 2 }}
              className="pointer-events-none absolute -bottom-1/3 -right-1/4 w-[65%] h-[65%] rounded-full opacity-40"
              style={{
                background: "radial-gradient(circle, hsl(var(--accent) / 0.4) 0%, transparent 65%)",
                filter: "blur(70px)",
              }}
            />
            {/* Glass refraction sheen — diagonal sweep */}
            <motion.div
              aria-hidden
              initial={{ x: "-150%" }}
              animate={{ x: "150%" }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", repeatDelay: 2 }}
              className="pointer-events-none absolute inset-y-0 w-1/2 opacity-30"
              style={{
                background: "linear-gradient(105deg, transparent 30%, hsl(0 0% 100% / 0.12) 50%, transparent 70%)",
              }}
            />
            {/* Inner gradient tint */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-[32px]"
              style={{
                background:
                  "linear-gradient(140deg, hsl(var(--primary)/0.10) 0%, transparent 45%, hsl(var(--accent)/0.08) 100%)",
              }}
            />
            {/* Top edge highlight */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-8 top-0 h-px"
              style={{ background: "linear-gradient(90deg, transparent, hsl(0 0% 100% / 0.5), transparent)" }}
            />
            {/* Bottom edge subtle highlight */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-12 bottom-0 h-px"
              style={{ background: "linear-gradient(90deg, transparent, hsl(var(--primary) / 0.4), transparent)" }}
            />
            <div className="relative z-10">
            {/* Eyebrow row — refined, no marquee noise */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-8 flex flex-wrap items-center justify-center gap-2 sm:gap-2.5"
            >
              {eyebrowItems.map((it) => {
                const Icon = it.icon;
                return (
                  <span
                    key={it.label}
                    className="liquid-glass-subtle inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground/85"
                  >
                    <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-primary relative z-10" />
                    <span className="relative z-10">{it.label}</span>
                  </span>
                );
              })}
            </motion.div>

            {/* Main Headline */}
            <div className="relative mb-8">
              {!isMobile && (
                <motion.div
                  animate={{ opacity: [0.25, 0.5, 0.25], scale: [1, 1.06, 1] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute inset-0 blur-[100px] pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(ellipse at center, hsl(var(--primary) / 0.35) 0%, hsl(var(--accent) / 0.15) 50%, transparent 70%)",
                  }}
                />
              )}
              <h1
                className="relative text-4xl sm:text-5xl md:text-6xl lg:text-[5.25rem] xl:text-[6rem] font-heading font-bold leading-[1.02] tracking-[-0.025em] text-center"
              >
                <SplitChars
                  as="span"
                  text="FearlessXCR7"
                  className="block text-foreground"
                />
                <span className="block mt-2 text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[3.5rem] font-semibold text-foreground/85">
                  <SplitChars
                    as="span"
                    text="Community Infrastructure"
                    delay={0.25}
                    className="text-foreground"
                  />
                  <span className="text-foreground/60"> & </span>
                  <span className="relative inline-block">
                    <SplitChars
                      as="span"
                      text="Ecosystem Builder"
                      delay={0.5}
                      charClassName="text-gradient"
                    />
                    <motion.div
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ delay: 1.4, duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
                      className="absolute -bottom-1.5 left-0 right-0 h-[2px] bg-gradient-to-r from-primary via-accent to-primary rounded-full origin-left"
                    />
                  </span>
                </span>
              </h1>

              {/* Tagline */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.45 }}
                className="mt-6 sm:mt-8 text-center"
              >
                <p className="text-lg sm:text-xl md:text-2xl font-heading font-medium leading-snug tracking-tight">
                  <span className="text-foreground">Turning Audiences into Communities,</span>
                  <br className="hidden sm:block" />
                  <span className="text-foreground/75"> Communities into </span>
                  <span className="text-gradient font-semibold">Loyal Ecosystems</span>
                  <span className="text-foreground/75">.</span>
                </p>
              </motion.div>
            </div>

            <div className="mb-10" />

            {/* CTA Buttons — two clean actions */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
            >
              <Button
                variant="glow"
                size="xl"
                className="w-full sm:w-auto shadow-xl shadow-primary/25 btn-3d"
                asChild
              >
                <Link to="/submit-project">
                  <Sparkles className="w-5 h-5" />
                  Submit Your Project
                </Link>
              </Button>
              <Button
                variant="outline"
                size="xl"
                className="w-full sm:w-auto border-primary/40 hover:border-primary backdrop-blur-md bg-card/40"
                asChild
              >
                <a href="#solutions">
                  Explore Solutions
                  <ArrowRight className="w-5 h-5" />
                </a>
              </Button>
            </motion.div>

            {/* Micro-trust line */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="mt-8 text-[11px] sm:text-xs text-muted-foreground/70 uppercase tracking-[0.18em]"
            >
              Retainer-first · India-priced · Globally proposal-led
            </motion.p>
            </div>
          </TiltGlassCard>
        </div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
      >
        <div className="flex flex-col items-center gap-2 animate-bounce" style={{ animationDuration: '1.5s' }}>
          <span className="text-[10px] text-muted-foreground/60 tracking-[0.2em] uppercase font-medium">Scroll</span>
          <div className="w-6 h-10 rounded-full border border-primary/30 flex items-start justify-center p-2 backdrop-blur-sm bg-background/10">
            <motion.div
              animate={{ y: [0, 8, 0], opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              className="w-1 h-2.5 rounded-full bg-gradient-to-b from-primary to-accent"
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
}
